/* eslint-disable max-lines -- single responsibility: ATC toolbar owns the error-routing
   surface for sample-limit, dimension-loading, and out-of-stock toasts; splitting would
   fragment the per-error-class handler chain without reducing complexity. */
import { useAtom, useAtomValue } from "jotai";
import { Save, ShoppingCart } from "lucide-react";
import { cn } from "tailwind-variants";

import { refineArtworkOpenAtom } from "@design-lab/entities/artwork";
import { pinningModeAtom } from "@design-lab/entities/pinning";
import { getUserType } from "@design-lab/entities/user";
import {
  useAddToCartModal,
  useEditCart,
  useReviewPinningModal
} from "@design-lab/features/manage-cart";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { ShopifyRecipeButton } from "@design-lab/features/shopify-recipe";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { trackDesignLabCtaClicked } from "@design-lab/shared/lib/analytics";
import { useIsSample, useIsShopifyRecipeCreator } from "@design-lab/shared/lib/config";
import {
  addToCartLoadingAtom,
  saveDesignLoadingAtom,
  shopifyRecipeLoadingAtom
} from "@design-lab/shared/state";
import { Button, DesignLabErrorBoundary } from "@design-lab/shared/ui";
import { useNotification } from "@design-lab/shared/ui/notification";
import { hasGraphQLErrors } from "@design-lab/shared/lib/errorGuards";
import { DimensionLoadingError, OutOfStockError } from "@design-lab/shared/lib/errors";
import { redirectTo } from "common/utils/browser";
import { useCompositionValidation } from "../model/useCompositionValidation";

/**
 * Renders the Add to Cart toolbar with actions for adding the design to cart and optionally saving the project.
 *
 * The toolbar shows a primary Add to Cart button and, when not in edit mode or a sample, a Save Project button.
 * Buttons are disabled or show loading states based on validation, edit/save/add-to-cart loading state, and refine mode.
 * Submitting validates compositions, handles edit or add-to-cart flows, intercepts desktop green-screen verification, and surfaces sample-related errors via notifications.
 *
 * @returns A React element containing the Add to Cart button and the optional Save Project button, with wiring for validation, loading state, analytics tracking, green-screen interception, and sample-related error notifications.
 */
function AddToCartToolbarImpl() {
  const { isEditMode } = useEditMode();
  const { handleAddToCartClick, isAddingToCart } = useAddToCartModal();
  const { handleEditCart, isEditingCart } = useEditCart();
  const { isDesktop } = useResponsive();
  const validation = useValidation();
  const { validateCompositions } = useCompositionValidation();
  const isSample = useIsSample();
  const userType = getUserType();
  const { notify } = useNotification();
  const isSaveDesignLoading = useAtomValue(saveDesignLoadingAtom);
  const [isAddToCartLoading, setIsAddToCartLoading] = useAtom(addToCartLoadingAtom);
  const [isShopifyRecipeLoading] = useAtom(shopifyRecipeLoadingAtom);
  const { setIsSaveProjectVisible } = useSaveProject();

  const [isRefineMode, setIsRefineMode] = useAtom(refineArtworkOpenAtom);
  const isPinningMode = useAtomValue(pinningModeAtom);
  const { hasCompletedSessions, openModal: openReviewModal } = useReviewPinningModal();
  const isShopifyRecipeCreator = useIsShopifyRecipeCreator();

  const isSubmitting = isEditMode ? isEditingCart : isAddingToCart || isAddToCartLoading;
  const buttonText = isEditMode ? "SAVE CHANGES" : "ADD TO CART";
  const loadingText = isEditMode ? "SAVING..." : "ADDING...";

  const isAnyLoading = isSubmitting || isSaveDesignLoading || isShopifyRecipeLoading;

  const isDisabled =
    isAnyLoading ||
    isPinningMode ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading ||
    !validation.hasProduct ||
    !validation.isQuantityValid;

  const isSaveDesignDisabled =
    isAnyLoading || !validation.isArtworkUploaded || validation.isSomeArtworkLoading;

  const proceedWithAddToCart = async () => {
    trackDesignLabCtaClicked("add_to_cart");

    setIsAddToCartLoading(true);
    try {
      await handleAddToCartClick(false, () => redirectTo("/cart/buy"));
      // Don't reset to false on success - page is navigating away and we need
      // the atom to remain true to prevent auto-save error handlers from redirecting
    } catch (error: unknown) {
      setIsAddToCartLoading(false);

      const errorName = error instanceof Error ? error.name : undefined;
      const errorMessage = error instanceof Error ? error.message : undefined;

      // Handle CombinedGraphQLErrors which is thrown by Apollo Client with errorPolicy: 'none'
      if (errorName === "CombinedGraphQLErrors" || errorName === "ApolloError") {
        // The error message contains our sample validation messages
        if (errorMessage?.includes("sample") || errorMessage?.includes("Sample")) {
          notify({ title: "Sample Limit Reached", description: errorMessage, variant: "error" });
          return;
        }

        // MN-6686 P0 fix (v2): Apollo v4 wraps each per-GraphQL-error detail
        // under `error.errors` (NOT `error.graphQLErrors`). The structured-code
        // path below at line ~108 keys on `hasGraphQLErrors`, which returns
        // false for v4 — so the INVALID_PLACEMENT check there is unreachable
        // in production. The first QA run on b29a213d23d caught this: the
        // backend's 422 INVALID_PLACEMENT response escaped the catch block as
        // an uncaught `CombinedGraphQLErrors` with no toast. Extracting the
        // first error from the v4 shape here keeps the toast routing intact.
        const combinedErrors = (
          error as { errors?: Array<{ message?: string; extensions?: { code?: string } }> }
        ).errors;
        if (Array.isArray(combinedErrors) && combinedErrors.length > 0) {
          const firstError = combinedErrors[0];
          if (firstError?.extensions?.code === "INVALID_PLACEMENT") {
            notify({
              title: "Invalid Design",
              description:
                firstError?.message?.trim() ||
                "The artwork dimensions are invalid for this product.",
              variant: "error"
            });
            return;
          }
        }
      }

      // Also check for graphQLErrors property (different error structures)
      const graphQLErrors = hasGraphQLErrors(error) ? error.graphQLErrors : [];
      if (graphQLErrors.length > 0) {
        const firstError = graphQLErrors[0];
        const errorCode = firstError?.extensions?.code;

        if (
          errorCode === "SAMPLE_ALREADY_IN_CART" ||
          errorCode === "SAMPLE_ORDER_LIMIT_REACHED" ||
          errorCode === "SAMPLE_LIMIT_ONE_PER_CART"
        ) {
          notify({
            title: "Sample Limit Reached",
            description: firstError?.message ?? "",
            variant: "error"
          });
          return;
        }

        // MN-6686 P0 fix: Print::AddToCart raises ArgumentError for dimension
        // validation failures (artwork W×H > product max, sub-min, etc.) and
        // `Mutations::AddMadeToCart` translates that to GraphQL code
        // INVALID_PLACEMENT (status 422). Without this branch the error falls
        // through the instanceof chain below, re-throws at line ~168, and the
        // customer sees no toast — the ATC button looks broken.
        if (errorCode === "INVALID_PLACEMENT") {
          notify({
            title: "Invalid Design",
            // Truthy fallback (||, not ??) so empty / whitespace-only backend
            // messages also fall back to the actionable copy. `??` would
            // preserve "" and render a blank toast, which Codex caught in
            // the adversarial review.
            description:
              firstError?.message?.trim() || "The artwork dimensions are invalid for this product.",
            variant: "error"
          });
          return;
        }
      }

      // Handle dimension loading errors with a user-visible notification
      if (error instanceof DimensionLoadingError) {
        notify({
          title: "Please Wait",
          description: error.message,
          variant: "warning"
        });
        return;
      }

      // Handle out-of-stock rejections with a customer-friendly toast.
      // No items were added to the cart; the customer must reduce quantities and retry.
      // (Raw variant IDs are intentionally omitted from the customer-facing copy —
      // the design-lab quantity selectors are the right place to surface per-variant
      // remaining counts. The structured oversold data on the thrown error remains
      // available for support tooling.)
      if (error instanceof OutOfStockError) {
        // Customer cares about how many *units* are short, not how many
        // distinct variants. `oversold.length` would render as "1 item
        // exceeds available stock" even when a customer asked for qty 50 of a
        // single variant with 3 available — confusing when paired with
        // "reduce the quantity and try again". Compute the actual shortfall
        // instead and fall back to a count-free message when shortfall data
        // is unavailable.
        const totalShortfall = error.oversold.reduce(
          (sum, entry) => sum + Math.max(entry.requestedQuantity - entry.availableQuantity, 0),
          0
        );
        const unitNoun = totalShortfall === 1 ? "unit" : "units";
        const description =
          totalShortfall > 0
            ? `Your design exceeds available stock by ${totalShortfall} ${unitNoun}. ` +
              "Please reduce the quantity and try again."
            : "Some items in your design exceed available stock. " +
              "Please reduce the quantity and try again.";
        notify({
          title: "Some Items Are Out of Stock",
          description,
          variant: "error"
        });
        return;
      }

      // Re-throw other errors to maintain existing error handling
      throw error;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateCompositions();
    if (!isValid) return;

    if (isRefineMode) {
      setIsRefineMode(false);
    }

    const proceedAction = isEditMode ? handleEditCart : proceedWithAddToCart;

    // Show review modal if there are completed pinning sessions
    if (hasCompletedSessions) {
      openReviewModal(proceedAction);
      return;
    }

    await proceedAction();
  };

  const handleSaveAndStartNew = async () => {
    trackDesignLabCtaClicked("save_and_start_new_design");
    setIsSaveProjectVisible(true);
  };

  return (
    <div className="flex w-full flex-col-reverse items-center gap-2 lg:w-auto lg:flex-row lg:gap-4">
      {!isEditMode && !isSample && (
        <div className="flex w-full gap-2">
          <Button
            data-testid="save-start-new-button"
            onPress={userType === "guest_user" ? () => {} : handleSaveAndStartNew}
            variant="outline"
            size={isDesktop ? "3xl" : "xl"}
            className={
              cn(
                "order-2 w-full border border-blue-700 lg:order-none lg:h-12 lg:w-auto lg:min-w-48 xl:h-16",
                userType === "guest_user" && "js-sign-in-modal-toggle"
              )!
            }
            isDisabled={isSaveDesignDisabled}
          >
            <Save className="size-5 lg:size-6" />
            Save Project
          </Button>
          {isShopifyRecipeCreator && (
            <ShopifyRecipeButton isAnyLoading={isAnyLoading} isDesktop={isDesktop} />
          )}
        </div>
      )}
      {!isShopifyRecipeCreator && (
        <Button
          data-testid="add-to-cart-button"
          onPress={handleSubmit}
          variant="destructive"
          size={isDesktop ? "3xl" : "xl"}
          isDisabled={isDisabled}
          className="order-1 w-full lg:order-none lg:h-12 lg:w-auto lg:min-w-48 xl:h-16"
        >
          <ShoppingCart className="size-5 lg:size-6" />
          {isSubmitting ? loadingText : buttonText}
        </Button>
      )}
    </div>
  );
}

export function AddToCartToolbar() {
  return (
    <DesignLabErrorBoundary boundary="DesignLabAddToCart">
      <AddToCartToolbarImpl />
    </DesignLabErrorBoundary>
  );
}
